import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { StringListEditor } from 'src/components/shared';
import { cn } from 'src/lib/utils';
import type { CreativeTemplate, CreativeTemplateWritePayload } from '../types/creative-template.types';
import {
  buildCreativeTemplatePayload,
  DEFAULT_ART_DIRECTION,
  DEFAULT_COMPLIANCE,
  DEFAULT_FALLBACK,
  DEFAULT_GENERATION_POLICY,
  validateCreativeTemplateForm,
} from '../helpers/creative-template.helpers';
import { BreakpointSpecsEditor } from './breakpoint-specs-editor';
import { TemplateRoleSpecsEditor } from './template-role-specs-editor';
import { SchedulerDsButton } from './scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';
const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
);

interface CreativeTemplateFormProps {
  initialTemplate?: CreativeTemplate | null;
  isCreate?: boolean;
  isSaving?: boolean;
  submitError?: string | null;
  onSubmit: (values: CreativeTemplateWritePayload) => void;
  onCancel?: () => void;
}

export function CreativeTemplateForm({
  initialTemplate,
  isCreate = false,
  isSaving = false,
  submitError = null,
  onSubmit,
  onCancel,
}: CreativeTemplateFormProps) {
  const payload = initialTemplate?.payload;
  const [templateId, setTemplateId] = useState(payload?.template_id ?? '');
  const [name, setName] = useState(payload?.name ?? '');
  const [roles, setRoles] = useState(payload?.roles ?? []);
  const [breakpoints, setBreakpoints] = useState(payload?.breakpoints ?? []);
  const [dropRoles, setDropRoles] = useState(payload?.fallback.drop_roles ?? DEFAULT_FALLBACK.drop_roles);
  const [promoteRoles, setPromoteRoles] = useState(payload?.fallback.promote_roles ?? DEFAULT_FALLBACK.promote_roles);
  const [fallbackBackground, setFallbackBackground] = useState(payload?.fallback.background ?? DEFAULT_FALLBACK.background);
  const [generationLevel, setGenerationLevel] = useState(payload?.generation_policy.level ?? DEFAULT_GENERATION_POLICY.level);
  const [maxGeneratedArea, setMaxGeneratedArea] = useState(payload?.generation_policy.max_generated_area_frac ?? DEFAULT_GENERATION_POLICY.max_generated_area_frac);
  const [requireProvenance, setRequireProvenance] = useState(payload?.generation_policy.require_provenance ?? DEFAULT_GENERATION_POLICY.require_provenance);
  const [requiresDisclaimer, setRequiresDisclaimer] = useState(payload?.compliance.requires_disclaimer ?? DEFAULT_COMPLIANCE.requires_disclaimer);
  const [minLegalFontPx, setMinLegalFontPx] = useState(payload?.compliance.min_legal_font_px ?? DEFAULT_COMPLIANCE.min_legal_font_px);
  const [disclaimerRole, setDisclaimerRole] = useState(payload?.compliance.disclaimer_role ?? DEFAULT_COMPLIANCE.disclaimer_role);
  const [typePairing, setTypePairing] = useState(payload?.art_direction.type_pairing ?? DEFAULT_ART_DIRECTION.type_pairing ?? '');
  const [paletteStrategy, setPaletteStrategy] = useState(payload?.art_direction.palette_strategy ?? DEFAULT_ART_DIRECTION.palette_strategy);
  const [changeNote, setChangeNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const values = buildCreativeTemplatePayload({
      ...(isCreate ? { template_id: templateId } : {}),
      name,
      roles,
      breakpoints,
      fallback: {
        drop_roles: dropRoles,
        promote_roles: promoteRoles,
        background: fallbackBackground,
      },
      generation_policy: {
        level: generationLevel,
        max_generated_area_frac: maxGeneratedArea,
        require_provenance: requireProvenance,
      },
      compliance: {
        requires_disclaimer: requiresDisclaimer,
        min_legal_font_px: minLegalFontPx,
        disclaimer_role: disclaimerRole,
      },
      art_direction: {
        type_pairing: typePairing || null,
        palette_strategy: paletteStrategy,
      },
      change_note: changeNote,
    });
    const error = validateCreativeTemplateForm(values, { isCreate });
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    onSubmit(values);
  };

  const displayError = validationError ?? submitError;

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {displayError ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {displayError}
        </p>
      ) : null}

      {isCreate ? (
        <label className="block space-y-1">
          <span className={labelClassName}>Template ID</span>
          <Input value={templateId} onChange={(event) => setTemplateId(event.target.value)} placeholder="e.g. generic_ribbon" />
        </label>
      ) : null}

      <label className="block space-y-1">
        <span className={labelClassName}>Name</span>
        <Input value={name} onChange={(event) => setName(event.target.value)} />
      </label>

      <TemplateRoleSpecsEditor values={roles} onChange={setRoles} />
      <BreakpointSpecsEditor values={breakpoints} onChange={setBreakpoints} />

      <section className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-4">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Fallback</h3>
        <StringListEditor label="Drop roles" values={dropRoles} onChange={setDropRoles} />
        <StringListEditor label="Promote roles" values={promoteRoles} onChange={setPromoteRoles} />
        <label className="relative block space-y-1">
          <span className={labelClassName}>Background</span>
          <select value={fallbackBackground} onChange={(event) => setFallbackBackground(event.target.value as typeof fallbackBackground)} className={selectClassName}>
            <option value="brand_solid">brand_solid</option>
            <option value="brand_gradient">brand_gradient</option>
            <option value="sampled_solid">sampled_solid</option>
          </select>
        </label>
      </section>

      <section className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-4">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Generation policy</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="relative block space-y-1">
            <span className={labelClassName}>Level</span>
            <select value={generationLevel} onChange={(event) => setGenerationLevel(event.target.value as typeof generationLevel)} className={selectClassName}>
              <option value="none">none</option>
              <option value="background_only">background_only</option>
              <option value="full">full</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className={labelClassName}>Max generated area frac</span>
            <Input type="number" step="0.01" value={maxGeneratedArea} onChange={(event) => setMaxGeneratedArea(Number(event.target.value))} />
          </label>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={requireProvenance} onChange={(event) => setRequireProvenance(event.target.checked)} className="size-4 rounded border-gray-400" />
          <span className={labelClassName}>Require provenance</span>
        </label>
      </section>

      <section className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-4">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Compliance & art direction</h3>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={requiresDisclaimer} onChange={(event) => setRequiresDisclaimer(event.target.checked)} className="size-4 rounded border-gray-400" />
          <span className={labelClassName}>Requires disclaimer</span>
        </label>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-1">
            <span className={labelClassName}>Min legal font px</span>
            <Input type="number" value={minLegalFontPx} onChange={(event) => setMinLegalFontPx(Number(event.target.value))} />
          </label>
          <label className="block space-y-1">
            <span className={labelClassName}>Disclaimer role</span>
            <Input value={disclaimerRole} onChange={(event) => setDisclaimerRole(event.target.value)} />
          </label>
          <label className="block space-y-1">
            <span className={labelClassName}>Type pairing</span>
            <Input value={typePairing} onChange={(event) => setTypePairing(event.target.value)} />
          </label>
          <label className="relative block space-y-1">
            <span className={labelClassName}>Palette strategy</span>
            <select value={paletteStrategy} onChange={(event) => setPaletteStrategy(event.target.value as typeof paletteStrategy)} className={selectClassName}>
              <option value="brand_exact">brand_exact</option>
              <option value="brand_derived">brand_derived</option>
              <option value="sampled">sampled</option>
            </select>
          </label>
        </div>
      </section>

      {!isCreate ? (
        <label className="block space-y-1">
          <span className={labelClassName}>Change note</span>
          <Input value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="Describe what changed in this version" />
        </label>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <SchedulerDsButton type="submit" disabled={isSaving}>
          {isSaving ? <Loader2 className="size-4 animate-spin" /> : null}
          {isCreate ? 'Create template' : 'Save new version'}
        </SchedulerDsButton>
        {onCancel ? (
          <SchedulerDsButton type="button" variant="secondary" disabled={isSaving} onClick={onCancel}>
            Cancel
          </SchedulerDsButton>
        ) : null}
      </div>
    </form>
  );
}
